import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown, LogIn, Wrench, User } from "lucide-react";
import { Logo } from "@/components/Logo";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { ThemeToggle } from "@/components/ThemeToggle";
import { EmergencyDepannage } from "@/components/EmergencyDepannage";
import { useLang } from "@/context/LanguageContext";
import { useClientAuth } from "@/context/ClientAuthContext";
import { api } from "@/lib/api";

export function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [svcOpen, setSvcOpen] = useState(false);
  const [services, setServices] = useState([]);
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useLang();
  const { user: clientUser } = useClientAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); setSvcOpen(false); }, [location.pathname]);
  useEffect(() => { api.get("/services").then(({ data }) => setServices(data)).catch(() => {}); }, []);

  const goSection = (id) => {
    setSvcOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 120);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const bookService = (id) => { setSvcOpen(false); navigate("/booking", { state: { service: id } }); };

  const links = [
    { label: t("nav.scan"), id: "scan" },
    { label: t("nav.contact"), id: "contact" },
  ];

  const linkCls = "font-mono-tech text-xs uppercase tracking-[0.14em] text-zinc-400 hover:text-white transition-colors";

  return (
    <header className="fixed top-0 inset-x-0 z-50 px-3 sm:px-5 pt-3">
      <div className={`nav-bar ${scrolled ? "scrolled" : ""} max-w-6xl mx-auto rounded-2xl px-4 sm:px-6 h-16 flex items-center justify-between transition-colors duration-300`}>
        <Logo />

        <nav className="hidden lg:flex items-center gap-7">
          {/* Services dropdown */}
          <div className="relative" onMouseEnter={() => setSvcOpen(true)} onMouseLeave={() => setSvcOpen(false)}>
            <button data-testid="nav-services" className={`${linkCls} flex items-center gap-1`}>
              {t("nav.services")} <ChevronDown className={`w-3.5 h-3.5 transition-transform ${svcOpen ? "rotate-180" : ""}`} />
            </button>
            {svcOpen && (
              <div className="nav-dropdown absolute left-1/2 -translate-x-1/2 top-full pt-3 w-72" data-testid="nav-services-dropdown">
                <div className="rounded-xl overflow-hidden border border-white/10 p-2">
                  {services.map((s) => (
                    <button key={s.id} onClick={() => bookService(s.id)} data-testid={`nav-service-${s.id}`}
                      className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-white/5 rounded-lg transition-colors group">
                      <span className="w-8 h-8 shrink-0 flex items-center justify-center border border-[#E60000]/40 bg-[#E60000]/10 rounded-md">
                        <Wrench className="w-4 h-4 text-[#E60000]" />
                      </span>
                      <span className="min-w-0">
                        <span className="block font-display font-700 text-white text-sm truncate">{s.label}</span>
                        <span className="block text-zinc-500 text-[11px] truncate">{s.duration}</span>
                      </span>
                    </button>
                  ))}
                  <button onClick={() => goSection("services")} data-testid="nav-services-all"
                    className="w-full mt-1 px-3 py-2.5 text-center font-mono-tech text-[11px] uppercase tracking-[0.15em] text-[#00E5FF] hover:bg-[#00E5FF]/10 rounded-lg transition-colors">
                    {t("nav.services_all")}
                  </button>
                </div>
              </div>
            )}
          </div>

          {links.map((l) => (
            <button key={l.id} onClick={() => goSection(l.id)} data-testid={`nav-${l.id}`} className={linkCls}>
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <LanguageSwitcher />
          <ThemeToggle className="rounded-full" />
          <EmergencyDepannage variant="nav" />
          <Link to={clientUser ? "/account" : "/login"} data-testid="nav-login" className="inline-flex items-center gap-1.5 font-mono-tech text-xs uppercase tracking-[0.14em] text-zinc-400 hover:text-white transition-colors">
            {clientUser ? <User className="w-4 h-4" /> : <LogIn className="w-4 h-4" />} {clientUser ? (clientUser.name?.split(" ")[0] || t("portal.account")) : t("nav.login")}
          </Link>
          <Link to="/booking" data-testid="nav-booking-cta"
            className="font-display font-700 text-sm px-5 py-2.5 rounded-full bg-gradient-to-r from-[#E60000] to-[#FF3B3B] text-white uppercase tracking-wide shadow-[0_0_26px_-6px_rgba(230,0,0,0.7)] hover:shadow-[0_0_34px_-4px_rgba(230,0,0,0.9)] transition-shadow">
            {t("nav.booking")}
          </Link>
        </div>

        <button className="lg:hidden text-white" onClick={() => setOpen((o) => !o)} data-testid="nav-mobile-toggle" aria-label="Menu">
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden nav-dropdown max-w-6xl mx-auto mt-2 rounded-2xl border border-white/10 px-5 py-6 flex flex-col gap-4" data-testid="nav-mobile-menu">
          <button onClick={() => goSection("services")} className="text-left font-mono-tech text-sm uppercase tracking-[0.15em] text-zinc-300" data-testid="nav-mobile-services">{t("nav.services")}</button>
          {links.map((l) => (
            <button key={l.id} onClick={() => goSection(l.id)} className="text-left font-mono-tech text-sm uppercase tracking-[0.15em] text-zinc-300" data-testid={`nav-mobile-${l.id}`}>
              {l.label}
            </button>
          ))}
          <Link to={clientUser ? "/account" : "/login"} className="font-mono-tech text-sm uppercase tracking-[0.15em] text-zinc-300 flex items-center gap-2" data-testid="nav-mobile-login">{clientUser ? <User className="w-4 h-4" /> : <LogIn className="w-4 h-4" />} {clientUser ? (clientUser.name?.split(" ")[0] || t("portal.account")) : t("nav.login")}</Link>
          <div className="flex items-center gap-3 pt-1">
            <LanguageSwitcher className="self-start" />
            <ThemeToggle className="rounded-full" />
          </div>
          <Link to="/booking" className="text-center font-display font-700 px-5 py-3 rounded-full bg-gradient-to-r from-[#E60000] to-[#FF3B3B] text-white uppercase tracking-wide" data-testid="nav-mobile-booking-cta">
            {t("nav.booking")}
          </Link>
          <EmergencyDepannage variant="navMobile" />
        </div>
      )}
    </header>
  );
}
